<template>
  <section class="GroupList">
    <main class="GroupList__main">
      <ScrollBox class="GroupList__scrollBox" always>
        <Card class="GroupList__card">
          <section class="GroupList__mainSection">
            <div class="GroupList__inner">
              <SearchForm
                id="search"
                class="GroupList__searchForm"
                name="search"
                :placeholder="messages.group.groupName"
                :value="searchWord"
                @inputWord="searchWord = $event"
              />
              <div class="GroupList__button">
                <Button
                  tag="button"
                  dynamics="primary"
                  :label="messages.group.createGroup"
                  @click="openSideContents"
                />
              </div>
            </div>
            <SelectedSettings
              v-if="checkedItemsCount"
              v-model:isTableChecked="isThChecked"
              v-model:isAllSelected="isAllSelected"
              class="GroupList__selectedSettings"
              :selectedNum="checkedItemsCount"
              :allNum="totalCheckboxCount"
              :itemsPerPage="MAX_DISPLAY_NUM"
              :labelName="messages.group.deleteGroup"
              :onDeleteButtonClick="openDeleteGroupModal"
              @update:isAllSelected="toggleAllChecked"
            />
            <template v-if="displayGroups.length !== 0 || isListLoading">
              <div class="GroupList__tableDiv">
                <ScrollBox always> </ScrollBox>
                <Table class="GroupList__table">
                  <thead>
                    <tr>
                      <th class="GroupList__head" scope="col">
                        <CheckBox
                          id="group"
                          name="group"
                          :aria-label="messages.common.selectAll"
                          :value="isThChecked"
                          :isChecked="isThChecked"
                          @change="onCheckedHeader($event)"
                        />
                      </th>
                      <th
                        v-for="head in headList"
                        :key="head"
                        class="GroupList__head"
                        scope="col"
                      >
                        {{ head }}
                      </th>
                      <th class="GroupList__head" scope="col">
                        <span class="GroupList__hidden">Menu</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-if="!isListLoading">
                      <tr
                        v-for="(item, index) in displayGroups"
                        :key="index"
                        :class="{
                          [`GroupList__row--check`]: item.isChecked,
                        }"
                      >
                        <td class="GroupList__tableItem">
                          <CheckBox
                            v-if="!item.isDefault"
                            :id="`${item.groupName ?? ''}__check`"
                            :name="`${item.groupName ?? ''}__check`"
                            :aria-label="messages.common.select"
                            :value="item.isChecked"
                            :isChecked="item.isChecked"
                            @change="onChecked($event, item)"
                          />
                        </td>
                        <td
                          class="GroupList__tableItemName"
                          :class="{
                            [`GroupList__tableItemName--highlight`]:
                              item.isDefault,
                          }"
                          :title="item.groupName"
                          @click="openGroupManagement(item)"
                        >
                          {{ item.groupName }}
                        </td>
                        <td
                          class="GroupList__tableItem"
                          :title="String(item.groupCount)"
                        >
                          {{ item.groupCount }}
                        </td>
                        <td
                          class="GroupList__tableItem GroupList__tableItem--icon"
                        >
                          <ActionMenu
                            :key="item.groupId"
                            v-model:visible="visibleActions[item.groupId]"
                            class="GroupList__actions"
                            :items="
                              MENU_BUTTON_ITEMS(item.groupId, item.isDefault)
                            "
                          />
                        </td>
                      </tr>
                    </template>
                    <template v-else>
                      <tr
                        v-for="skeleton in 12"
                        :key="skeleton"
                        class="GroupList__skeletonWrapper"
                      >
                        <td class="GroupList__skeleton" colspan="4">
                          <TableSkeleton />
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </Table>
              </div>
              <div class="GroupList__information">
                {{ messages.common.referenceMark }}
                <span v-if="defaultGroup" class="GroupList__highlight">{{
                  defaultGroup
                }}</span>
                <span v-else class="GroupList__infoSkeleton shimmer"></span>
                {{ messages.group.info.defaultGroup }}
              </div>
              <Loading v-if="isListLoading" />
            </template>
            <template v-else-if="displayGroups.length == 0 && !isListLoading">
              <NoData
                :text="messages.group.info.noDataText"
                :img-url="noSearchDataImage"
              />
            </template>
            <PaginationBar
              class="GroupList__pagination"
              :currentPage="currentPage"
              :maxDisplayPages="maxDisplayPages"
              :itemsPerPage="MAX_DISPLAY_NUM"
              :total="totalCount"
              @onClick:pageNumber="movePage"
            />
            <SideContents
              :heading="messages.group.createGroup"
              :isOpen="isSideContentsOpen"
              :buttonList="buttonList"
              @update:close="closeSideContents"
            >
              <Card class="GroupList__groupCard">
                <Heading
                  class="GroupList__title"
                  type="tertiary"
                  level="2"
                  :heading="messages.group.basicSettings"
                />
                <Table class="GroupList__table">
                  <tbody>
                    <tr>
                      <th class="GroupList__heading" scope="col">
                        <span class="GroupList__label">
                          {{ messages.group.groupName }}
                        </span>
                      </th>
                      <td class="GroupList__item">
                        <TextField
                          id="groupName"
                          name="groupName"
                          type="text"
                          :ariaLabel="messages.group.groupName"
                          :placeholder="messages.group.placeholder.groupName"
                          :isError="isError"
                          :errorText="
                            isEmptyError
                              ? commonMessage.requiredField(
                                  messages.group.groupName
                                )
                              : isLengthError
                              ? commonMessage.lengthValidationError(
                                  messages.group.groupName,
                                  50
                                )
                              : ''
                          "
                          :value="groupItem.groupName"
                          @input="onChangeGroupName($event.target.value)"
                        >
                        </TextField>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Card>
            </SideContents>
          </section>
        </Card>
      </ScrollBox>
    </main>
  </section>
  <CreateGroupModal
    v-if="isCreateGroupModalOpen"
    :isError="isCreateError"
    @onClickModalClose="onClickCreateModalClose"
  />
  <UpdateGroupModal
    v-if="isUpdateGroupModalOpen"
    :groupName="groupItem.groupName"
    :isError="isUpdateError"
    @onClickModalClose="onClickUpdateModalClose"
    @updateGroup="onClickUpdateGroup"
  />
  <DeleteGroupModal
    v-if="isDeleteGroupModalOpen"
    :isError="isDeleteError"
    :isGroupInUse="isGroupInUse"
    @onClickModalClose="closeGroupDeleteModal"
    @deleteGroupItems="onClickDeleteGroups"
  />
</template>

<script lang="ts">
export default {
  name: 'cctoolGroups',
};
</script>

<script setup lang="ts">
import Heading from '@spectee/components/Atoms/Heading/index.vue';
import Card from '@spectee/components/Atoms/Card/index.vue';
import Button from '@spectee/components/Atoms/Button/index.vue';
import CreateGroupModal from '@spectee/components/Organisms/CreateGroupModal/index.vue';
import UpdateGroupModal from '@spectee/components/Organisms/UpdateGroupModal/index.vue';
import DeleteGroupModal from '@spectee/components/Organisms/DeleteGroupModal/index.vue';
import SearchForm from '@spectee/components/Atoms/SearchForm/index.vue';
import TableSkeleton from '@spectee/components/Molecules/TableSkeleton/index.vue';
import Table from '@spectee/components/Atoms/Table/index.vue';
import SideContents from '@spectee/components/Organisms/SideContents/index.vue';
import TextField from '@spectee/components/Atoms/TextField/index.vue';
import PaginationBar from '@spectee/components/Molecules/PaginationBar/index.vue';
import ActionMenu from '@spectee/components/Molecules/ActionMenu/index.vue';
import CheckBox from '@spectee/components/Atoms/CheckBox/index.vue';
import SelectedSettings from '@spectee/components/Molecules/SelectedSettings/index.vue';
import { useListGroups } from '@spectee/graphql/queries/listGroups';
import {
  computed,
  ref,
  Ref,
  watch,
  watchEffect,
  onMounted,
  onUnmounted,
} from 'vue';
import { useOverlayLoading } from '@spectee/composables/useOverlayLoading';
import { useUpdateGroup } from '@spectee/graphql/mutations/updateGroup';
import { useDeleteGroups } from '@spectee/graphql/mutations/deleteGroups';
import { useCreateGroup } from '@spectee/graphql/mutations/createGroup';
import {
  MutationCreateGroupArgs,
  MutationUpdateGroupArgs,
  MutationDeleteGroupsArgs,
  Message,
} from '@spectee/graphql/api';
import { useRoute, useRouter } from 'vue-router';
import { useFeedback } from '@spectee/composables/useFeedback';
import { commonMessage } from '@spectee/const/messageContents';
import { lengthValidate } from '@spectee/ts/validation';
import noSearchDataImage from '@spectee/assets/images/img_search_noItem.svg';
import NoData from '@spectee/components/Molecules/NoData/index.vue';
import messages from '../../../../messages.json';
import { DialogButtonItem } from '@spectee/types/atoms';

const route = useRoute();
const router = useRouter();

const { pushFeedback } = useFeedback();

const queryParams = route.query;

type GroupItem = {
  groupId: string;
  groupName: string;
  groupCount: number;
  isDefault: boolean;
  isChecked: boolean;
};

const searchWord = ref('');
const isCreateGroupModalOpen = ref(false);
const isUpdateGroupModalOpen = ref(false);
const isDeleteGroupModalOpen = ref(false);

const headList = [messages.group.groupName, messages.group.numberOfMembers];
const isSideContentsOpen = ref(false);
const groupItem = ref<GroupItem>({
  groupId: '',
  groupName: '',
  groupCount: 0,
  isDefault: false,
  isChecked: false,
});

const MAX_DISPLAY_NUM = 50;
const maxDisplayPages = 10;
const currentPage = ref(1);

const isThChecked = ref(false);
const isAllSelected = ref(false);
const checkedItemsCount = ref(0);
const totalCount = ref(0);
const totalCheckboxCount = ref(0);

const isUpdateError = ref<boolean>(false);
const isDeleteError = ref<boolean>(false);
const isError = ref<boolean>(false);
const isEmptyError = ref<boolean>(false);
const isLengthError = ref<boolean>(false);

const defaultGroup = ref('');

const groupItems: Ref<GroupItem[]> = ref([]);
const {
  listGroupsRes,
  loading: isListLoading,
  refetch: refetchListGroups,
} = useListGroups();

const filteredGroups = computed(() => {
  if (searchWord.value) {
    const regex = new RegExp(searchWord.value, 'i');
    return groupItems.value.filter(groupItem =>
      regex.test(groupItem.groupName)
    );
  }
  return groupItems.value;
});

const openSideContents = async () => {
  groupItem.value = {
    groupId: '',
    groupName: '',
    groupCount: 0,
    isDefault: false,
    isChecked: false,
  };
  isSideContentsOpen.value = true;
};

const closeSideContents = () => {
  isSideContentsOpen.value = false;
  isError.value = false;
};

const movePage = (PageNumber: number) => {
  currentPage.value = PageNumber;
  closeAllVisibleActions();
};

watch(
  () => totalCount.value,
  () => {
    const totalPages = Math.ceil(totalCount.value / MAX_DISPLAY_NUM);

    // Adjust current page if needed (After deleting users)
    if (currentPage.value > totalPages) {
      currentPage.value = Math.max(totalPages, 1); // fallback to page 1 if all pages are gone
    }
  }
);

const start = computed(() => (currentPage.value - 1) * MAX_DISPLAY_NUM);
const end = computed(() =>
  Math.min(start.value + MAX_DISPLAY_NUM, totalCount.value)
);

const displayGroups = computed(() =>
  filteredGroups.value.slice(start.value, end.value)
);

const checkedFilterItemsLength = () => {
  return filteredGroups.value?.filter(group => group.isChecked).length;
};

const checkedDisplayItemsLength = () => {
  return displayGroups.value.filter(group => group.isChecked).length;
};

const checkboxDisplayItemsLength = () => {
  return displayGroups.value.filter(group => !group.isDefault).length;
};

watchEffect(() => {
  checkedItemsCount.value = checkedFilterItemsLength();
  totalCount.value = filteredGroups.value.length;
  totalCheckboxCount.value =
    filteredGroups.value?.filter(group => !group.isDefault).length || 0;
});

watch(
  () => route.query,
  () => {
    if (Object.keys(route.query).length === 0) {
      router.push({
        path: route.path,
        query: { ...queryParams }, // Preserve existing query params
      });
    }
  }
);

watchEffect(() => {
  groupItems.value = (listGroupsRes.value ?? []).map(group => {
    if (group.isDefault) {
      defaultGroup.value = group.name ?? '';
    }
    return {
      groupId: group.id,
      groupName: group.name,
      groupCount: group.memberCount,
      isDefault: group.isDefault,
      isChecked: false,
    } as GroupItem;
  });
});

watchEffect(() => {
  const checkedDisplayGroupsCount = checkedDisplayItemsLength();
  const checkboxDisplayItemsCount = checkboxDisplayItemsLength();
  if (checkedDisplayGroupsCount === checkboxDisplayItemsCount) {
    isThChecked.value = true;
  } else {
    isThChecked.value = false;
  }
});

const onCheckedHeader = (event: Event) => {
  const { target } = event;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }
  groupItems.value.forEach(group => {
    if (displayGroups.value.includes(group) && !group.isDefault) {
      group.isChecked = target.checked;
    }
  });
  checkedItemsCount.value = checkedFilterItemsLength();
};

const onChecked = (event: Event, checkedGroup: GroupItem) => {
  const { target } = event;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }
  groupItems.value.forEach(group => {
    if (checkedGroup.groupId === group.groupId) {
      group.isChecked = target.checked;
    }
  });

  checkedItemsCount.value = checkedFilterItemsLength();
  const checkedDisplayGroupsCount = checkedDisplayItemsLength();
  const checkboxDisplayItemsCount = checkboxDisplayItemsLength();
  isThChecked.value =
    checkedDisplayGroupsCount === checkboxDisplayItemsCount ? true : false;
};

const toggleAllChecked = (isAllChecked: boolean) => {
  groupItems.value.forEach(group => {
    if (filteredGroups.value.includes(group) && !group.isDefault) {
      group.isChecked = isAllChecked;
    }
  });
  isThChecked.value = isAllChecked;
  isAllSelected.value = isAllChecked;
};

const onChangeGroupName = (groupName: string) => {
  groupItem.value.groupName = groupName;
  validateGroupName(groupName);
};

const validateGroupName = (groupName: string) => {
  isEmptyError.value = !groupName || groupName.trim() === '';
  if (!isEmptyError.value) {
    const isValidLength = lengthValidate(groupName, 50);
    isLengthError.value = !isValidLength; // Set length error flag if length exceeds 50
  } else {
    isLengthError.value = false; // Reset length error if conditions are met
  }
  isError.value = isEmptyError.value || isLengthError.value;
};

const onClickCreateModalClose = () => {
  isCreateGroupModalOpen.value = false;
  isCreateError.value = false;
};
const { mutate: createGroup, loading: createGroupLoading } = useCreateGroup();

const isCreateError = ref(false);

useOverlayLoading('createGroupLoading', createGroupLoading);

const addGroup = async () => {
  validateGroupName(groupItem.value.groupName);
  if (isError.value) {
    document.getElementById('groupName')?.focus();
    return;
  }
  const params: MutationCreateGroupArgs = {
    input: {
      name: groupItem.value.groupName,
    },
  };

  try {
    const resp = await createGroup(params);

    if (resp && !Object.prototype.hasOwnProperty.call(resp, 'errors')) {
      if (resp.data.createGroup.message) {
        isCreateGroupModalOpen.value = true;
      } else {
        pushFeedback({
          type: 'success',
          heading: messages.group.success.createGroup,
        });
        refetchListGroups();
      }
      closeSideContents();
    } else {
      isCreateGroupModalOpen.value = true;
      isCreateError.value = true;
    }
  } catch (e) {
    isCreateGroupModalOpen.value = true;
    isCreateError.value = true;
    console.error(e);
  }
};

const buttonList = ref<DialogButtonItem[]>([
  {
    tag: 'button',
    dynamics: 'secondary',
    label: messages.common.cancel,
    click: closeSideContents,
  },
  {
    tag: 'button',
    dynamics: 'primary',
    label: messages.group.create,
    click: addGroup,
  },
]);

const handleEditClick = (groupId: number | string) => {
  closeAllVisibleActions();
  isUpdateGroupModalOpen.value = true;
  isUpdateError.value = false;
  const selectedGroup = groupItems.value.find(
    group => group.groupId === groupId
  );
  if (!selectedGroup) {
    // No matching group found
    isUpdateGroupModalOpen.value = false;
    return;
  }

  groupItem.value = selectedGroup;
};

const handleDeleteClick = (groupId: number | string) => {
  closeAllVisibleActions();
  const selectedGroup = groupItems.value.find(
    group => group.groupId === groupId
  );

  if (!selectedGroup) {
    return;
  }

  groupItem.value = selectedGroup;
  isDeleteSingleGroup.value = true;
  isDeleteGroupModalOpen.value = true;
};

const MENU_BUTTON_ITEMS = (groupId: number | string, isDefault: boolean) => {
  const editItem = {
    text: messages.common.change,
    iconName: 'Edit',
    isDelete: false,
    onClick: () => handleEditClick(groupId),
  };

  const deleteItem = {
    text: messages.common.delete,
    isDelete: true,
    onClick: () => handleDeleteClick(groupId),
  };

  return isDefault ? [editItem] : [editItem, deleteItem];
};

const visibleActions = ref<{ [key: number | string]: boolean }>({});

const closeAllVisibleActions = () => {
  Object.keys(visibleActions.value).forEach(key => {
    visibleActions.value[Number(key)] = false;
  });
};

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!target.closest('.GroupList__actions')) {
    closeAllVisibleActions();
  }
};
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

const onClickUpdateModalClose = () => {
  isUpdateGroupModalOpen.value = false;
};

const { mutate: updateGroup, loading: updateGroupLoading } = useUpdateGroup();

useOverlayLoading('updateGroupLoading', updateGroupLoading);

const onClickUpdateGroup = async (groupName: string) => {
  const params: MutationUpdateGroupArgs = {
    input: {
      id: groupItem.value.groupId,
      name: groupName,
    },
  };

  try {
    const resp = await updateGroup(params);
    if (resp?.data?.updateGroup?.statusCode === Message.success) {
      pushFeedback({
        type: 'success',
        heading: messages.group.success.updateGroup,
      });
      onClickUpdateModalClose();
      refetchListGroups();
    } else {
      isUpdateError.value = true;
    }
  } catch (e) {
    isUpdateError.value = true;
    console.error(e);
  }
};

const closeGroupDeleteModal = () => {
  isDeleteGroupModalOpen.value = false;
  isGroupInUse.value = false;
  isDeleteError.value = false;
  isDeleteSingleGroup.value = false;
};

const openDeleteGroupModal = () => {
  isDeleteGroupModalOpen.value = true;
};

const { mutate: deleteGroup, loading: deleteGroupLoading } = useDeleteGroups();

useOverlayLoading('deleteGroupLoading', deleteGroupLoading);

const isGroupInUse = ref<boolean>(false);
const isDeleteSingleGroup = ref<boolean>(false);

const onClickDeleteGroups = async () => {
  if (isDeleteSingleGroup.value) {
    if (groupItem.value.groupCount > 0) {
      isGroupInUse.value = true;
      return;
    }
    deleteGroups([groupItem.value.groupId]);
  } else {
    isGroupInUse.value = groupItems.value.some(
      group => group.isChecked && group.groupCount > 0
    );
    if (isGroupInUse.value) {
      return;
    }
    const groupIds = groupItems.value
      .filter(group => group.isChecked)
      .map(group => group.groupId);
    deleteGroups(groupIds);
  }
};

const deleteGroups = async (groupIds: string[]) => {
  const params: MutationDeleteGroupsArgs = {
    input: {
      groupIds: groupIds,
    },
  };

  try {
    const resp = await deleteGroup(params);
    if (resp?.data?.deleteGroups?.statusCode === Message.success) {
      closeGroupDeleteModal();
      pushFeedback({
        type: 'success',
        heading: messages.group.success.deleteGroup,
      });
      refetchListGroups();
    } else {
      isDeleteError.value = true;
    }
  } catch (e) {
    isDeleteError.value = true;
    console.error(e);
  }
};

const openGroupManagement = (item: GroupItem) => {
  router.push({
    name: 'AdminGroupManagement',
    state: {
      groupId: item.groupId,
      groupName: item.groupName,
      isDefault: item.isDefault,
    },
  });
};
</script>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_pageForward.scss' as *;

.GroupList {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;

  &__button {
    margin-left: auto;
  }

  & &__main {
    position: relative;
    display: flex;
    height: 100%;
    overflow: hidden;
    padding: 0;
  }

  &__card {
    height: 100%;
    padding: $service-unit * 5 $service-unit * 6;
    background-color: $color-gray-20;
  }

  &__inner {
    display: flex;
    align-items: center;
    margin: $service-unit * 6 0;
  }

  & &__searchForm {
    width: 100% * calc(400 / 1104);
    max-width: $service-unit * 100;
  }

  & &__scrollBox {
    flex: 1;
    overflow: hidden;
  }

  & &__mainSection {
    padding: $service-unit * 4 $service-unit * 4 $service-unit * 6;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: auto;
    background-color: $color-white-100;
  }

  &__tableDiv {
    overflow-x: auto;
    height: auto;
    min-height: $service-unit * 50;
  }

  &__head {
    @include table-head;
    position: sticky;
    top: 0;
    z-index: 2;

    &:first-child {
      width: $service-unit * 11;
    }
  }

  &__tableItem,
  &__tableItemName {
    @include table-item;

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:nth-child(2),
    &:nth-child(3) {
      @include ellipsis;
    }

    &--icon {
      position: sticky;
      right: 0;
      max-width: 100%;
      padding: 0;
    }
  }

  &__tableItemName {
    cursor: pointer;
    color: $color-blue-60;

    &--highlight {
      color: #ca52b0;
      font-weight: bold;
    }
  }

  &__skeletonWrapper:not(:first-child) &__skeleton {
    padding-top: $service-unit * 6;
  }

  &__information {
    display: block;
    font-size: $service-font-size-mp;
    margin: $service-unit * 3 0;
  }

  &__infoSkeleton {
    display: inline-block;
    vertical-align: middle;
    width: 15%;
    height: $service-unit * 5;
    background-color: $color-gray-20;
    border-radius: $service-border-radius * 2;
  }

  &__highlight {
    display: contents;
    white-space: noWrap;
    color: #ca52b0;
    font-weight: bold;
  }

  &__heading {
    @include text-title;

    width: $service-unit * 45;
    white-space: normal;
    word-break: break-word;
  }

  &__label {
    display: flex;
    column-gap: $service-unit;
    align-items: center;
  }

  &__item {
    @include text-data;
    padding: 0 $service-unit * 4;
  }

  & &__groupCard {
    box-shadow: unset;
  }

  &__title {
    font-size: $service-font-size-p;
    margin: $service-unit * 4 0;
  }

  & &__pagination {
    margin-top: $service-unit * 5;
  }

  &__selectedSettings {
    margin-bottom: $service-unit * 7;
  }

  &__hidden {
    @include hidden;
  }

  & &__actions {
    position: absolute;
    right: 0;
    top: 0;
  }

  @media (max-width: 768px) {
    &__infoSkeleton {
      width: 30%;
    }
  }

  @media (max-width: 767px) {
    &__mainSection {
      margin-left: $service-unit * 6;
    }

    & &__searchForm {
      width: 48%;
    }

    &__table {
      min-width: $service-unit * 110;
    }

    &__tableItem,
    &__tableItemName {
      font-size: $service-font-size-pp;
    }

    & &__actions {
      top: $service-unit;
    }

    &__tableItem--icon {
      position: relative;
      right: $service-unit * 36;
    }
  }
}
</style>
